import { Download, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type {
  PackagingItem,
  PackagingItemOutputDocument,
  PackagingItemOutputDocumentStatus,
} from '@/shared/hooks/api/packagingSuppliers'
import { useDownloadPackagingItemOutputDocument } from '@/shared/hooks/api/packagingSuppliers'
import { PackagingItemChangeHistoryTimestampCell } from './PackagingItemChangeHistoryTimestampCell'
import { PackagingItemDeclarationOfConformityExportButton } from './PackagingItemDeclarationOfConformityExportButton'

type PackagingItemOutputDocumentsTabProps = {
  packagingSupplierId: string
  item: PackagingItem
}

function outputDocumentStatusVariant(
  status: PackagingItemOutputDocumentStatus
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'COMPLETED') {
    return 'default'
  }
  if (status === 'FAILED') {
    return 'destructive'
  }
  return 'secondary'
}

function OutputDocumentActorCell({
  document,
}: {
  document: PackagingItemOutputDocument
}) {
  const { t } = useTranslation('packagingSuppliers')
  const actorKey =
    document.createdByActor === 'SUPPLIER'
      ? 'itemDetail.outputDocuments.actor.supplier'
      : 'itemDetail.outputDocuments.actor.lex'
  const actorLabel = t(actorKey)
  const userLabel = document.createdByUserDisplayName

  if (userLabel) {
    return (
      <span className='text-sm'>
        {actorLabel}
        <span className='text-muted-foreground'> · {userLabel}</span>
      </span>
    )
  }

  return <span className='text-sm'>{actorLabel}</span>
}

function OutputDocumentActionsCell({
  packagingSupplierId,
  itemId,
  document,
}: {
  packagingSupplierId: string
  itemId: string
  document: PackagingItemOutputDocument
}) {
  const { t } = useTranslation('packagingSuppliers')
  const downloadMutation = useDownloadPackagingItemOutputDocument(
    packagingSupplierId,
    itemId
  )

  const isInFlight =
    document.status === 'PENDING' || document.status === 'PROCESSING'

  if (document.status === 'COMPLETED') {
    return (
      <Button
        type='button'
        variant='outline'
        size='sm'
        disabled={downloadMutation.isPending}
        onClick={() => {
          void downloadMutation
            .mutateAsync({
              outputDocumentId: document.id,
              fileName: document.fileName,
            })
            .catch(() => {
              toast.error(t('itemDetail.outputDocuments.downloadFailed'))
            })
        }}
      >
        <Download className='size-3.5' />
        {t('itemDetail.outputDocuments.download')}
      </Button>
    )
  }

  if (isInFlight) {
    return (
      <Button type='button' variant='outline' size='sm' disabled>
        <Loader2 className='size-3.5 animate-spin' />
        {t('itemDetail.outputDocuments.generating')}
      </Button>
    )
  }

  return <span className='text-sm text-muted-foreground'>—</span>
}

export function PackagingItemOutputDocumentsTab({
  packagingSupplierId,
  item,
}: PackagingItemOutputDocumentsTabProps) {
  const { t } = useTranslation('packagingSuppliers')
  const documents = item.outputDocuments ?? []

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <p className='text-sm text-muted-foreground'>
          {t('itemDetail.outputDocuments.description')}
        </p>
        <PackagingItemDeclarationOfConformityExportButton
          packagingSupplierId={packagingSupplierId}
          item={item}
        />
      </div>

      {documents.length === 0 ? (
        <p className='py-8 text-center text-sm text-muted-foreground'>
          {t('itemDetail.outputDocuments.empty')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {t('itemDetail.outputDocuments.columns.status')}
              </TableHead>
              <TableHead>
                {t('itemDetail.outputDocuments.columns.declarationNumber')}
              </TableHead>
              <TableHead>
                {t('itemDetail.outputDocuments.columns.createdBy')}
              </TableHead>
              <TableHead>
                {t('itemDetail.outputDocuments.columns.createdAt')}
              </TableHead>
              <TableHead className='text-right'>
                {t('itemDetail.outputDocuments.columns.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((document) => (
              <TableRow key={document.id}>
                <TableCell>
                  <div className='space-y-1'>
                    <Badge
                      variant={outputDocumentStatusVariant(document.status)}
                      className='inline-flex items-center gap-1.5'
                    >
                      {(document.status === 'PENDING' ||
                        document.status === 'PROCESSING') && (
                        <Loader2 className='size-3 animate-spin' />
                      )}
                      {t(
                        `itemDetail.outputDocuments.statuses.${document.status}`
                      )}
                    </Badge>
                    {document.status === 'FAILED' &&
                      document.errorMessage != null &&
                      document.errorMessage.trim() !== '' && (
                        <p className='text-xs text-destructive'>
                          {document.errorMessage}
                        </p>
                      )}
                  </div>
                </TableCell>
                <TableCell className='font-mono text-xs'>
                  {document.declarationNumber ?? '—'}
                </TableCell>
                <TableCell>
                  <OutputDocumentActorCell document={document} />
                </TableCell>
                <TableCell>
                  <PackagingItemChangeHistoryTimestampCell
                    timestamp={document.createdAt}
                  />
                </TableCell>
                <TableCell className='text-right'>
                  <OutputDocumentActionsCell
                    packagingSupplierId={packagingSupplierId}
                    itemId={item.id}
                    document={document}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
